import { Fragment, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { App, Button, Empty, Input, Modal, Segmented, Space, Spin, Typography } from "antd";
import { formatNok } from "@gull/shared";
import { useDeliveryActions, type OfferResponse } from "../../lib/useDeliveryActions";
import ArrowIcon from "../../components/ArrowIcon";
import ChevronIcon from "../../components/ChevronIcon";
import ImageThumb from "../../components/ImageThumb";
import { useDeliveries } from "./hooks/useDeliveries";
import type { Delivery, SettlementType } from "./delivery";

const { Paragraph, Text } = Typography;

export function DeliveriesTab({
  uid,
  focusedOfferCode,
}: {
  uid: string;
  focusedOfferCode: string | null;
}) {
  const deliveries = useDeliveries(uid);

  if (deliveries === null) return <Spin />;

  if (deliveries.length === 0) {
    return (
      <Empty
        image={null}
        description={
          <Space direction="vertical" size={4}>
            <Text>Du har ingen innleveringer ennå.</Text>
            <Text type="secondary">
              Bestill en gullkonvolutt eller lever gullsmykker i butikk for vurdering.
            </Text>
          </Space>
        }
        style={{ padding: 32 }}
      >
        <Link to="/bestill-pose">
          <Button type="primary">Bestill gullkonvolutt</Button>
        </Link>
      </Empty>
    );
  }
  return (
    <>
      <div className="minside-delivery-actions">
        <Link to="/bestill-pose">
          <Button className="minside-action-button">
            Bestill ny gullkonvolutt <ArrowIcon />
          </Button>
        </Link>
      </div>
      <div className="minside-delivery-list">
        {deliveries.map((delivery) => (
          <DeliveryCard
            key={`${delivery.deliveryMethod}:${delivery.id}`}
            delivery={delivery}
            initiallyExpanded={
              !!focusedOfferCode && delivery.reference?.toUpperCase() === focusedOfferCode
            }
          />
        ))}
      </div>
    </>
  );
}

function formatGrams(value: number | null | undefined): string {
  if (value == null) return "—";
  return `${value.toLocaleString("nb-NO", { maximumFractionDigits: 2 })} gram`;
}

/** The frame's `20.AUGUST 13:05` — no year, and uppercased by the stylesheet. */
function formatDeliveryDate(timestamp: number): string {
  const parts = new Intl.DateTimeFormat("nb-NO", {
    timeZone: "Europe/Oslo",
    day: "2-digit",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  }).formatToParts(new Date(timestamp));
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((candidate) => candidate.type === type)?.value ?? "";
  return `${part("day")}.${part("month")} ${part("hour")}:${part("minute")}`;
}

function DeliveryFact({
  label,
  value,
  place,
}: {
  label: string;
  value: string;
  place: "lead" | "fill" | "full";
}) {
  return (
    <div className={`minside-delivery-fact minside-delivery-fact--${place}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function DeliveryCard({
  delivery,
  initiallyExpanded = false,
}: {
  delivery: Delivery;
  initiallyExpanded?: boolean;
}) {
  const { message } = App.useApp();
  const cardRef = useRef<HTMLElement | null>(null);
  const [expanded, setExpanded] = useState(initiallyExpanded);
  const [localResponse, setLocalResponse] = useState<OfferResponse | null>(null);
  const [responseDraft, setResponseDraft] = useState<{
    response: OfferResponse;
    settlementType: SettlementType;
    comment: string;
  } | null>(null);
  const { responding, respond: answerOffer } = useDeliveryActions({
    reference: delivery.reference,
  });

  useEffect(() => {
    setLocalResponse(null);
    setResponseDraft(null);
  }, [delivery.deliveryMethod, delivery.id, delivery.state.code]);

  useEffect(() => {
    if (!initiallyExpanded) return;
    setExpanded(true);
    window.setTimeout(() => {
      cardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  }, [initiallyExpanded]);

  const totalOffer = delivery.totalOffer;
  const canRespond = delivery.canRespond && !localResponse;

  const copyReference = () => {
    if (!delivery.reference) return;
    void navigator.clipboard?.writeText(delivery.reference);
    message.success("Kode kopiert");
  };

  const submitResponse = async () => {
    if (!responseDraft) return;
    const result = await answerOffer(
      responseDraft.response,
      responseDraft.settlementType,
      responseDraft.comment,
    );
    if (!result.ok) return;
    setLocalResponse(responseDraft.response);
    setResponseDraft(null);
  };

  const openResponse = (response: OfferResponse) =>
    setResponseDraft({ response, settlementType: delivery.settlementType, comment: "" });

  type Fact = { label: string; value: string };
  const factRows: Array<{ lead: Fact | null; fill: Fact | null }> = [];
  for (const row of delivery.assessmentRows) {
    factRows.push({
      lead: { label: "Karat", value: `${row.karat} karat` },
      fill: { label: "Vekt veid gull", value: formatGrams(row.weightGrams) },
    });
  }
  // The facts after the karat rows fill the two-column grid in order, so they are paired up rather
  // than each being given a fixed column — a deduction row would otherwise leave a hole.
  const offerFacts: Fact[] = [];
  if (totalOffer !== null) offerFacts.push({ label: "Tilbud", value: formatNok(totalOffer) });
  if (delivery.deductionNok != null && delivery.deductionNok > 0) {
    offerFacts.push({
      label: "Fratrekk steinfjerning",
      value: `−${formatNok(delivery.deductionNok)}`,
    });
  }
  if (delivery.stoneHandling) {
    offerFacts.push({ label: "Stener, perler eller ur", value: delivery.stoneHandling });
  }
  for (let index = 0; index < offerFacts.length; index += 2) {
    factRows.push({ lead: offerFacts[index] ?? null, fill: offerFacts[index + 1] ?? null });
  }
  // Order data alone is not an assessment, so it must not stand in for one that has not happened.
  const awaitingAssessment = delivery.assessmentRows.length === 0 && totalOffer === null;

  return (
    <>
      <article className="minside-delivery-card" ref={cardRef}>
        <button
          type="button"
          className="minside-delivery-head"
          aria-expanded={expanded}
          onClick={() => setExpanded((current) => !current)}
        >
          <div className="minside-delivery-thumb">
            {delivery.imagePath ? (
              <ImageThumb path={delivery.imagePath} />
            ) : (
              <div className="sendin-image-strip-placeholder">Ingen bilder</div>
            )}
          </div>
          <div className="minside-delivery-stack">
            <h2>{delivery.title}</h2>
            <span>{formatDeliveryDate(delivery.createdAt)}</span>
            {delivery.reference ? (
              <span
                className="minside-copy-code"
                role="button"
                tabIndex={0}
                onClick={(event) => {
                  event.stopPropagation();
                  copyReference();
                }}
                onKeyDown={(event) => {
                  if (event.key !== "Enter" && event.key !== " ") return;
                  event.preventDefault();
                  event.stopPropagation();
                  copyReference();
                }}
              >
                {delivery.reference}
              </span>
            ) : null}
            <div className="minside-delivery-count">
              <span>
                {delivery.itemCount} gjenstand{delivery.itemCount === 1 ? "" : "er"}
              </span>
              <span className="minside-status-tag">{delivery.state.label}</span>
            </div>
          </div>
          <span className="minside-card-chevron" aria-hidden="true">
            <ChevronIcon direction={expanded ? "up" : "down"} />
          </span>
        </button>
        {expanded ? (
          <div className="minside-delivery-details">
            {awaitingAssessment ? <Text type="secondary">{delivery.state.message}</Text> : null}
            {factRows.length > 0 ? (
              <div className="minside-delivery-facts">
                {factRows.map(({ lead, fill }, index) => (
                  <Fragment key={index}>
                    {lead ? <DeliveryFact {...lead} place={fill ? "lead" : "full"} /> : null}
                    {fill ? <DeliveryFact {...fill} place={lead ? "fill" : "full"} /> : null}
                  </Fragment>
                ))}
              </div>
            ) : null}
            {canRespond ? (
              <div className="minside-delivery-buttons">
                <Button
                  className="minside-action-button"
                  loading={responding === "ACCEPTED"}
                  disabled={!!responding || totalOffer === 0}
                  onClick={() => openResponse("ACCEPTED")}
                >
                  Godta tilbud <ArrowIcon />
                </Button>
                <Button
                  className="minside-ghost-button"
                  loading={responding === "DECLINED"}
                  disabled={!!responding}
                  onClick={() => openResponse("DECLINED")}
                >
                  Avslå tilbud
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}
      </article>
      <Modal
        className="customer-response-modal"
        title={
          responseDraft?.response === "DECLINED"
            ? "Avslå tilbudet på hele innleveringen?"
            : "Aksepter tilbudet på hele innleveringen?"
        }
        open={responseDraft !== null}
        onCancel={() => setResponseDraft(null)}
        onOk={() => void submitResponse()}
        okText={responseDraft?.response === "DECLINED" ? "Avslå" : "Aksepter"}
        okButtonProps={{ danger: responseDraft?.response === "DECLINED" }}
        confirmLoading={responding !== null}
        cancelText="Avbryt"
      >
        <Paragraph type="secondary">
          {responseDraft?.response === "DECLINED"
            ? "Vi håndterer hele innleveringen for retur. Du får e-post når innleveringen er klargjort for henting, og den må hentes innen 3 måneder etter at e-posten er sendt. Legg gjerne ved en kommentar."
            : "Velg om du ønsker oppgjør som utbetaling til bankkonto eller gavekort hos David-Andersen. Du kan legge ved en kommentar før du godkjenner."}
        </Paragraph>
        {responseDraft?.response === "ACCEPTED" && delivery.goldValue != null ? (
          <div style={{ marginBottom: 16 }}>
            <Text strong style={{ display: "block", marginBottom: 8 }}>
              Velg oppgjør: utbetaling eller gavekort
            </Text>
            <Segmented
              block
              value={responseDraft.settlementType}
              onChange={(value) =>
                setResponseDraft((current) =>
                  current ? { ...current, settlementType: value as SettlementType } : current,
                )
              }
              options={[
                {
                  value: "PAYOUT",
                  label: `Utbetaling ${formatNok(delivery.offerForSettlement("PAYOUT"))}`,
                },
                {
                  value: "GIFT_CARD",
                  label: `Gavekort ${formatNok(delivery.offerForSettlement("GIFT_CARD"))}`,
                },
              ]}
            />
          </div>
        ) : null}
        <Input.TextArea
          rows={3}
          value={responseDraft?.comment ?? ""}
          onChange={(event) =>
            setResponseDraft((current) =>
              current ? { ...current, comment: event.target.value } : current,
            )
          }
          placeholder="Kommentar (valgfritt)"
          maxLength={1000}
          showCount
        />
      </Modal>
    </>
  );
}
